import { notesApi, type NoteDetail } from '@/api/notes';
import {
  bytesToBase64,
  encryptSym,
  generateDEK,
  strToBytes,
  wrapKey,
  type KeyPair,
} from '@/crypto/sodium';
import { wrapDekForNoteShares } from '@/features/notes/noteShareWraps';

/**
 * Criptează HTML-ul cu un DEK nou și îl trimite la server: update (cu re-wrap
 * pentru toate share-urile) dacă `noteId` există, altfel create.
 *
 * `parentId` e părintele curent al notiței la update, respectiv părintele sub
 * care se creează notița la create.
 */
export async function encryptAndSaveNote(args: {
  html: string;
  title: string;
  keyPair: KeyPair;
  tenantId: string;
  noteId: string | null;
  parentId: string | null;
  userId: string | undefined;
}): Promise<NoteDetail> {
  const { html, title, keyPair, tenantId, noteId, parentId, userId } = args;
  const plaintext = strToBytes(html);
  const dek = await generateDEK();
  const { ciphertext, nonce } = await encryptSym(plaintext, dek);

  try {
    if (noteId !== null) {
      // Re-wrap DEK pt TOȚI shares (evităm drift la multi-share)
      const wraps = await wrapDekForNoteShares({
        noteId,
        tenantId,
        dek,
        keyPair,
        userId,
      });
      return notesApi.update(noteId, {
        title,
        parent_id: parentId,
        ciphertext: bytesToBase64(ciphertext),
        nonce: bytesToBase64(nonce),
        ...wraps,
      });
    }
    // Create — doar wrap pt caller (owner inițial)
    const wrapped = await wrapKey(dek, keyPair.publicKey);
    return notesApi.create(tenantId, {
      title,
      parent_id: parentId,
      ciphertext: bytesToBase64(ciphertext),
      nonce: bytesToBase64(nonce),
      dek_wrapped: bytesToBase64(wrapped),
    });
  } finally {
    // zeroize DEK best-effort
    dek.fill(0);
  }
}
